import { useState, useEffect } from 'react';
import adminService from '../../../services/adminService';
import AdminUserDetail from './AdminUserDetail';
import './AdminUsers.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [newMaxTimelines, setNewMaxTimelines] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', maxTimelines: 5, role: 'user', permissions: [], canCreateGroup: false, maxEditors: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminService.getAllUsers();
      // Sadece normal kullanıcıları göster (admin olmayanlar)
      setUsers(data.filter((u) => !u.isAdmin && !u.isSuperAdmin));
    } catch (err) {
      setError('Kullanıcılar yüklenemedi. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleUpdateMaxTimelines = async (userId) => {
    try {
      const maxTimelines = parseInt(newMaxTimelines, 10);
      if (isNaN(maxTimelines) || maxTimelines < 0) {
        setError('Lütfen geçerli bir sayı girin.');
        return;
      }
      await adminService.updateUserMaxTimelines(userId, maxTimelines);
      setEditingUser(null);
      setNewMaxTimelines('');
      fetchUsers();
    } catch (err) {
      setError('Güncelleme başarısız. ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`"${username}" kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) return;
    try {
      setError('');
      await adminService.deleteUser(userId);
      setSuccess(`"${username}" silindi.`);
      fetchUsers();
    } catch (err) {
      setError('Silme başarısız. ' + (err.response?.data?.message || err.message));
    }
  };

  const handleMakeAdmin = async (userId, username) => {
    if (!window.confirm(`"${username}" kullanıcısını admin yapmak istediğinizden emin misiniz?`)) return;
    try {
      setError('');
      await adminService.updateUserRole(userId, { isAdmin: true });
      setSuccess(`"${username}" artık admin.`);
      fetchUsers();
    } catch (err) {
      setError('Yetkilendirme başarısız. ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditUser = (user) => {
    setEditUserData({
      _id: user._id,
      username: user.username,
      email: user.email,
      maxTimelines: user.maxTimelines,
      isAdmin: user.isAdmin || false,
      isSuperAdmin: user.isSuperAdmin || false,
      newPassword: '',
      canCreateGroup: user.canCreateGroup || false,
      maxEditors: user.maxEditors || 0,
      permissions: user.permissions || [],
      role: user.role || 'user',
    });
    setShowEditForm(true);
    setEditingUser(null);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      // Profil güncelle
      const profileData = {};
      if (editUserData.username) profileData.username = editUserData.username;
      if (editUserData.email) profileData.email = editUserData.email;
      if (editUserData.newPassword) profileData.password = editUserData.newPassword;
      
      if (Object.keys(profileData).length > 0) {
        await adminService.updateUserProfile(editUserData._id, profileData);
      }
      // Limit güncelle
      await adminService.updateUserMaxTimelines(editUserData._id, parseInt(editUserData.maxTimelines, 10));
      // Rol güncelle
      await adminService.updateUserRole(editUserData._id, { 
        isAdmin: editUserData.isAdmin, 
        isSuperAdmin: editUserData.isSuperAdmin || false 
      });
      // Yetki güncelle
      await adminService.updateUserPermissions(editUserData._id, {
        permissions: editUserData.permissions,
        canCreateGroup: editUserData.canCreateGroup,
        maxEditors: parseInt(editUserData.maxEditors, 10) || 0,
        role: editUserData.role,
      });
      setSuccess(`"${editUserData.username}" güncellendi.`);
      setShowEditForm(false);
      setEditUserData(null);
      fetchUsers();
    } catch (err) {
      setError('Güncelleme başarısız. ' + (err.response?.data?.message || err.message));
    }
  };

  const togglePermission = (perm) => {
    const current = editUserData.permissions || [];
    if (current.includes(perm)) {
      setEditUserData({ ...editUserData, permissions: current.filter(p => p !== perm) });
    } else {
      setEditUserData({ ...editUserData, permissions: [...current, perm] });
    }
  };

  // Detay sayfasına geç
  const handleShowDetail = (user) => {
    setShowDetail(user);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newUser.username || !newUser.email || !newUser.password) {
      setError('Tüm alanları doldurun.');
      return;
    }

    try {
      await adminService.createAdminUser({
        username: newUser.username,
        email: newUser.email,
        password: newUser.password,
        maxTimelines: parseInt(newUser.maxTimelines, 10) || 5,
        isAdmin: false,
        isSuperAdmin: false,
        role: newUser.role,
        permissions: newUser.permissions,
        canCreateGroup: newUser.canCreateGroup,
        maxEditors: parseInt(newUser.maxEditors, 10) || 0,
      });
      setSuccess(`"${newUser.username}" kullanıcısı eklendi.`);
      setShowAddForm(false);
      setNewUser({ username: '', email: '', password: '', maxTimelines: 5, role: 'user', permissions: [], canCreateGroup: false, maxEditors: 0 });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Kullanıcı eklenemedi.');
    }
  };

  const totalPages = Math.ceil(users.length / itemsPerPage);
  const paginatedUsers = users.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (loading) {
    return <div className="admin-users-loading">Yükleniyor...</div>;
  }

  // Detay sayfası açıksa onu göster
  if (showDetail) {
    return <AdminUserDetail user={showDetail} onBack={() => setShowDetail(null)} />;
  }

  return (
    <div className="admin-users">
      {success && <div className="admin-users-success">{success}</div>}
      {error && <div className="admin-users-error">{error}</div>}

      <div className="admin-users-card">
        <div className="admin-users-header">
          <h3>Kullanıcılar ({users.length})</h3>
          <button className="admin-users-add-btn" onClick={() => setShowAddForm(!showAddForm)}>
            + Yeni Kullanıcı Ekle
          </button>
        </div>

        {showAddForm && (
          <form className="admin-users-form" onSubmit={handleAddUser}>
            <h4>Yeni Kullanıcı Ekle</h4>
            <div className="admin-users-form-grid">
              <div className="admin-users-form-group">
                <label>Kullanıcı Adı</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  required
                />
              </div>
              <div className="admin-users-form-group">
                <label>E-posta</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </div>
              <div className="admin-users-form-group">
                <label>Şifre</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                />
              </div>
              <div className="admin-users-form-group">
                <label>Çizelge Limiti</label>
                <input
                  type="number"
                  value={newUser.maxTimelines}
                  onChange={(e) => setNewUser({ ...newUser, maxTimelines: e.target.value })}
                  min="0"
                />
              </div>
            </div>

            {/* Rol */}
            <div className="admin-users-form-group">
              <label>Rol</label>
              <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} style={{ width: '100%', padding: '0.5rem', backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '0.375rem', color: '#fff' }}>
                <option value="user">Kullanıcı</option>
                <option value="editor">Editör</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Süper Admin</option>
              </select>
            </div>

            {/* Grup Yetkisi */}
            <div className="admin-users-form-group" style={{ padding: '1rem', backgroundColor: '#1f2937', borderRadius: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', display: 'block' }}>Grup Yönetimi</label>
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label className="admin-users-checkbox">
                  <input type="checkbox" checked={newUser.canCreateGroup} onChange={(e) => setNewUser({ ...newUser, canCreateGroup: e.target.checked })} />
                  <span>Grup Kurma Yetkisi</span>
                </label>
                {newUser.canCreateGroup && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Editör Limiti:</label>
                    <input type="number" value={newUser.maxEditors} onChange={(e) => setNewUser({ ...newUser, maxEditors: e.target.value })} min="0" max="100" style={{ width: '4rem', padding: '0.25rem 0.5rem', backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '0.25rem', color: '#fff', fontSize: '0.8rem' }} />
                  </div>
                )}
              </div>
            </div>

            {/* Modül Yetkileri */}
            <div className="admin-users-form-group" style={{ padding: '1rem', backgroundColor: '#1f2937', borderRadius: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', display: 'block' }}>Modül Yetkileri</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {[
                  { key: 'slider', label: 'Slider Yönetimi' },
                  { key: 'boxes', label: 'Kutucuklar Yönetimi' },
                  { key: 'texts', label: 'Metin Bölümleri' },
                  { key: 'features', label: 'Özellikler' },
                  { key: 'user-management', label: 'Kullanıcı Yönetimi' },
                  { key: 'admin-management', label: 'Yönetici Yönetimi' },
                  { key: 'invitation-codes', label: 'Davet Kodları' },
                  { key: 'project-management', label: 'Proje Yönetimi' },
                ].map(mod => (
                  <label key={mod.key} className="admin-users-checkbox">
                    <input type="checkbox" checked={newUser.permissions.includes(mod.key)} onChange={() => {
                      const perms = newUser.permissions.includes(mod.key) ? newUser.permissions.filter(p => p !== mod.key) : [...newUser.permissions, mod.key];
                      setNewUser({ ...newUser, permissions: perms });
                    }} />
                    <span>{mod.label}</span>
                  </label>
                ))}
              </div>
              <label className="admin-users-checkbox" style={{ marginTop: '0.75rem', borderTop: '1px solid #374151', paddingTop: '0.75rem' }}>
                <input type="checkbox" checked={newUser.permissions.includes('all')} onChange={() => {
                  const perms = newUser.permissions.includes('all') ? newUser.permissions.filter(p => p !== 'all') : [...newUser.permissions, 'all'];
                  setNewUser({ ...newUser, permissions: perms });
                }} />
                <span style={{ fontWeight: '600' }}>Tüm Yetkiler</span>
              </label>
            </div>

            <div className="admin-users-form-actions-row">
              <button type="submit" className="admin-users-form-save">Ekle</button>
              <button type="button" className="admin-users-form-cancel" onClick={() => setShowAddForm(false)}>İptal</button>
            </div>
          </form>
        )}

        {showEditForm && editUserData && (
          <form className="admin-users-form" onSubmit={handleSaveEdit}>
            <h4>Kullanıcı Düzenle: {editUserData.username}</h4>
            <div className="admin-users-form-grid">
              <div className="admin-users-form-group">
                <label>Kullanıcı Adı</label>
                <input type="text" value={editUserData.username} onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })} />
              </div>
              <div className="admin-users-form-group">
                <label>E-posta</label>
                <input type="email" value={editUserData.email} onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })} />
              </div>
              <div className="admin-users-form-group">
                <label>Yeni Şifre (boş bırakırsan değişmez)</label>
                <input type="password" value={editUserData.newPassword} onChange={(e) => setEditUserData({ ...editUserData, newPassword: e.target.value })} placeholder="••••••" />
              </div>
              <div className="admin-users-form-group">
                <label>Çizelge Limiti</label>
                <input type="number" value={editUserData.maxTimelines} onChange={(e) => setEditUserData({ ...editUserData, maxTimelines: e.target.value })} min="0" />
              </div>
            </div>

            {/* Rol */}
            <div className="admin-users-form-group">
              <label>Rol</label>
              <select value={editUserData.role} onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })} style={{ width: '100%', padding: '0.5rem', backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '0.375rem', color: '#fff' }}>
                <option value="user">Kullanıcı</option>
                <option value="editor">Editör</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Süper Admin</option>
              </select>
            </div>

            {/* Grup Yetkisi */}
            <div className="admin-users-form-group" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#1f2937', borderRadius: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', display: 'block' }}>Grup Yönetimi</label>
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label className="admin-users-checkbox">
                  <input type="checkbox" checked={editUserData.canCreateGroup} onChange={(e) => setEditUserData({ ...editUserData, canCreateGroup: e.target.checked })} />
                  <span>Grup Kurma Yetkisi</span>
                </label>
                {editUserData.canCreateGroup && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Editör Limiti:</label>
                    <input type="number" value={editUserData.maxEditors} onChange={(e) => setEditUserData({ ...editUserData, maxEditors: e.target.value })} min="0" max="100" style={{ width: '4rem', padding: '0.25rem 0.5rem', backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '0.25rem', color: '#fff', fontSize: '0.8rem' }} />
                  </div>
                )}
              </div>
            </div>

            {/* Modül Yetkileri */}
            <div className="admin-users-form-group" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#1f2937', borderRadius: '0.5rem' }}>
              <label style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', display: 'block' }}>Modül Yetkileri (Erişebileceği Bölümler)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {[
                  { key: 'slider', label: 'Slider Yönetimi' },
                  { key: 'boxes', label: 'Kutucuklar Yönetimi' },
                  { key: 'texts', label: 'Metin Bölümleri' },
                  { key: 'features', label: 'Özellikler' },
                  { key: 'user-management', label: 'Kullanıcı Yönetimi' },
                  { key: 'admin-management', label: 'Yönetici Yönetimi' },
                  { key: 'invitation-codes', label: 'Davet Kodları' },
                  { key: 'project-management', label: 'Proje Yönetimi' },
                ].map(mod => (
                  <label key={mod.key} className="admin-users-checkbox">
                    <input type="checkbox" checked={(editUserData.permissions || []).includes(mod.key)} onChange={() => togglePermission(mod.key)} />
                    <span>{mod.label}</span>
                  </label>
                ))}
              </div>
              <label className="admin-users-checkbox" style={{ marginTop: '0.75rem', borderTop: '1px solid #374151', paddingTop: '0.75rem' }}>
                <input type="checkbox" checked={(editUserData.permissions || []).includes('all')} onChange={() => togglePermission('all')} />
                <span style={{ fontWeight: '600' }}>Tüm Yetkiler</span>
              </label>
            </div>

            {/* Eski Yetkilendirme */}
            <div className="admin-users-form-group" style={{ marginTop: '1rem' }}>
              <label>Yetkilendirme (Eski)</label>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <label className="admin-users-checkbox">
                  <input type="checkbox" checked={editUserData.isAdmin} onChange={(e) => setEditUserData({ ...editUserData, isAdmin: e.target.checked })} />
                  <span>Admin</span>
                </label>
                <label className="admin-users-checkbox">
                  <input type="checkbox" checked={editUserData.isSuperAdmin || false} onChange={(e) => setEditUserData({ ...editUserData, isSuperAdmin: e.target.checked })} />
                  <span>Süper Admin</span>
                </label>
              </div>
            </div>

            <div className="admin-users-form-actions-row">
              <button type="submit" className="admin-users-form-save">Kaydet</button>
              <button type="button" className="admin-users-form-cancel" onClick={() => { setShowEditForm(false); setEditUserData(null); }}>İptal</button>
            </div>
          </form>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table className="admin-users-table">
            <thead>
              <tr>
                <th>Kullanıcı Adı</th>
                <th>E-posta</th>
                <th>Çizelge (Oluşturulan / Maks)</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => (
                <tr key={user._id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    {user.timelineCount} /{' '}
                    {editingUser === user._id ? (
                      <input
                        type="number"
                        className="admin-users-edit-input"
                        value={newMaxTimelines}
                        onChange={(e) => setNewMaxTimelines(e.target.value)}
                        min="0"
                      />
                    ) : (
                      user.maxTimelines
                    )}
                  </td>
                  <td>
                    <div className="admin-users-actions">
                      {editingUser === user._id ? (
                        <>
                          <button className="admin-users-save-btn" onClick={() => handleUpdateMaxTimelines(user._id)}>Kaydet</button>
                          <button className="admin-users-cancel-btn" onClick={() => setEditingUser(null)}>İptal</button>
                        </>
                      ) : (
                        <>
                          <button
                            className="admin-users-edit-btn"
                            onClick={() => { setEditingUser(user._id); setNewMaxTimelines(user.maxTimelines); }}
                          >
                            Limit
                          </button>
                          <button
                            className="admin-users-edit-btn"
                            onClick={() => handleEditUser(user)}
                          >
                            Düzenle
                          </button>
                          <button
                            className="admin-users-edit-btn"
                            onClick={() => handleShowDetail(user)}
                            style={{ color: '#a78bfa' }}
                          >
                            Detay
                          </button>
                          <button
                            className="admin-users-delete-btn"
                            onClick={() => handleDeleteUser(user._id, user.username)}
                          >
                            Sil
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="admin-users-pagination">
            <button onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1}>Önceki</button>
            <span>Sayfa {currentPage} / {totalPages}</span>
            <button onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages}>Sonraki</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
